import React, { createContext, useContext, useEffect, ReactNode } from 'react'
import { useRouter } from 'next/router'
import useDeviceDetection, { DeviceType } from '../hooks/useDeviceDetection'

interface MobileContextType {
  deviceType: DeviceType
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  isHydrated: boolean
}

const MobileContext = createContext<MobileContextType | undefined>(undefined)

// Routes that should NOT trigger mobile redirect (auth, onboarding, etc.)
const EXCLUDED_ROUTES = ['/auth', '/onboarding', '/onboarding-v2']

const isExcludedRoute = (pathname: string): boolean => {
  return EXCLUDED_ROUTES.some(route => pathname.startsWith(route))
}

interface MobileProviderProps {
  children: ReactNode
}

export const MobileProvider: React.FC<MobileProviderProps> = ({ children }) => {
  const router = useRouter()
  const { deviceType, isMobile, isTablet, isDesktop, isHydrated } = useDeviceDetection()

  // Handle automatic redirects between mobile/desktop routes
  useEffect(() => {
    if (!isHydrated) return

    const currentPath = router.pathname
    const isOnMobileRoute = currentPath.startsWith('/mobile')

    // Skip redirect for excluded routes (auth, onboarding, etc.)
    if (isExcludedRoute(currentPath)) return

    // Redirect mobile users to /mobile if they're on a desktop route
    if (isMobile && !isOnMobileRoute) {
      router.replace('/mobile')
    }

    // Redirect desktop/tablet users away from /mobile route
    if (!isMobile && isOnMobileRoute) {
      router.replace('/')
    }
  }, [isMobile, isHydrated, router.pathname])

  const value: MobileContextType = {
    deviceType,
    isMobile,
    isTablet,
    isDesktop,
    isHydrated
  }

  return <MobileContext.Provider value={value}>{children}</MobileContext.Provider>
}

export const useMobileContext = (): MobileContextType => {
  const context = useContext(MobileContext)
  if (context === undefined) {
    throw new Error('useMobileContext must be used within a MobileProvider')
  }
  return context
}

export default MobileContext
